import React, { useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { FiPlus, FiShoppingBag, FiCheck, FiChevronDown, FiTrash2, FiEdit3, FiX } from 'react-icons/fi';

const ShopSelector = ({ shops, activeShopId, setActiveShopId, addShop, deleteShop, updateShopName }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const [newShopName, setNewShopName] = useState('');
  const [newShopType, setNewShopType] = useState('etsy');
  const [isOpen, setIsOpen] = useState(false);

  const activeShop = shops.find(s => s.id === activeShopId);

  // Helper to extract logo from shop progress
  const getShopLogo = (shop) => {
    if (!shop?.progress) return null;
    const logoData = shop.progress['brand-identity']?.['bi-2'];
    if (!logoData) return null;
    return logoData.imageList?.[0] || logoData.imageUrl;
  };

  const handleAddShop = (e) => {
    e.preventDefault();
    if (!newShopName.trim()) return;
    addShop(newShopName, newShopType);
    setNewShopName('');
    setShowAddForm(false);
  };

  const startEditingName = (e) => {
    e.stopPropagation();
    setEditNameValue(activeShop?.name || '');
    setIsEditingName(true);
    setIsOpen(false);
  };

  const saveNameEdit = (e) => {
    e.preventDefault();
    if (editNameValue.trim() && activeShopId) {
      updateShopName(activeShopId, editNameValue.trim());
    }
    setIsEditingName(false);
  };

  const activeLogo = getShopLogo(activeShop);

  return (
    <div className="relative">
      <div className="flex items-center gap-2">
        <div className="flex-1 relative">
          <button 
            onClick={() => setIsOpen(!isOpen)}
            className="w-full flex items-center justify-between bg-white/10 hover:bg-white/20 px-4 py-3 rounded-xl border border-white/10 transition-all text-cream"
          >
            <div className="flex items-center gap-3 overflow-hidden">
              <div className="w-8 h-8 rounded-lg bg-terracotta/20 flex items-center justify-center flex-shrink-0 overflow-hidden border border-white/10">
                {activeLogo ? (
                  <img src={activeLogo} alt="Shop Logo" className="w-full h-full object-cover" />
                ) : (
                  <SafeIcon icon={FiShoppingBag} className="text-terracotta w-4 h-4" />
                )}
              </div>
              <div className="text-left truncate">
                {isEditingName ? (
                  <div className="flex items-center" onClick={e => e.stopPropagation()}>
                    <input 
                      autoFocus
                      className="text-sm font-bold text-white leading-tight border-b border-terracotta focus:outline-none bg-transparent w-full"
                      value={editNameValue}
                      onChange={e => setEditNameValue(e.target.value)}
                      onBlur={saveNameEdit}
                      onKeyDown={e => e.key === 'Enter' && saveNameEdit(e)}
                    />
                  </div>
                ) : (
                  <h3 className="text-sm font-bold truncate flex items-center gap-2">
                    {activeShop ? activeShop.name : 'Select a Shop'}
                    <span className="text-[10px] font-normal text-cream/40 uppercase">({activeShop?.type})</span>
                  </h3>
                )}
              </div>
            </div>
            <SafeIcon icon={FiChevronDown} className={`text-cream/40 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
          </button>

          {isOpen && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-cream-dark z-50 overflow-hidden animate-fade-in">
              <div className="max-h-60 overflow-y-auto">
                {shops.map(shop => {
                  const logo = getShopLogo(shop);
                  return (
                    <div key={shop.id} className="group flex items-center">
                      <button 
                        onClick={() => {
                          setActiveShopId(shop.id);
                          setIsOpen(false);
                        }}
                        className={`flex-1 flex items-center justify-between px-4 py-3 hover:bg-cream/30 transition-colors ${activeShopId === shop.id ? 'bg-cream/50' : ''}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-6 h-6 rounded bg-cream-dark flex items-center justify-center overflow-hidden border border-cream-dark/50">
                            {logo ? (
                              <img src={logo} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <SafeIcon icon={FiShoppingBag} className="text-espresso-light/30 w-3 h-3" />
                            )}
                          </div>
                          <span className="text-xs font-bold text-espresso">{shop.name}</span>
                          <span className="text-[9px] uppercase px-1.5 py-0.5 bg-cream-dark/50 rounded text-espresso-light">{shop.type}</span>
                        </div>
                        {activeShopId === shop.id && <SafeIcon icon={FiCheck} className="text-sage w-3.5 h-3.5" />}
                      </button>
                      {shops.length > 1 && (
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteShop(shop.id);
                          }}
                          className="px-3 py-3 text-espresso-light/20 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                        >
                          <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              <button 
                onClick={() => {
                  setShowAddForm(true);
                  setIsOpen(false);
                }}
                className="w-full p-3 bg-cream/20 border-t border-cream-dark text-xs font-bold text-terracotta hover:bg-cream/50 transition-colors flex items-center justify-center gap-2"
              >
                <SafeIcon icon={FiPlus} /> Create New Shop
              </button>
            </div>
          )}
        </div>
        {!isEditingName && (
          <button 
            onClick={startEditingName}
            className="p-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-cream/40 hover:text-terracotta transition-colors"
            title="Edit shop name"
          >
            <SafeIcon icon={FiEdit3} className="w-4 h-4" />
          </button>
        )}
      </div>

      {showAddForm && (
        <div className="fixed inset-0 bg-espresso/80 backdrop-blur-md z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl animate-slide-up text-espresso">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-espresso">Setup New Shop</h2>
              <button onClick={() => setShowAddForm(false)} className="text-espresso-light/40 hover:text-espresso">
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleAddShop} className="space-y-6">
              <div>
                <label className="block text-sm font-bold text-espresso-light mb-2">Shop Name</label>
                <input 
                  autoFocus
                  className="w-full px-4 py-3 rounded-xl border border-cream-dark focus:border-terracotta focus:outline-none bg-cream/20 text-espresso placeholder:text-espresso-light/30"
                  placeholder="e.g. Vintage Vibes Apparel"
                  value={newShopName}
                  onChange={e => setNewShopName(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-espresso-light mb-2">Platform</label>
                <div className="grid grid-cols-2 gap-4">
                  <button 
                    type="button" 
                    onClick={() => setNewShopType('etsy')}
                    className={`py-3 px-4 rounded-xl border-2 font-bold transition-all ${newShopType === 'etsy' ? 'border-terracotta bg-terracotta/5 text-terracotta' : 'border-cream-dark text-espresso-light hover:border-cream-dark/60'}`}
                  >
                    Etsy
                  </button>
                  <button 
                    type="button" 
                    onClick={() => setNewShopType('shopify')}
                    className={`py-3 px-4 rounded-xl border-2 font-bold transition-all ${newShopType === 'shopify' ? 'border-terracotta bg-terracotta/5 text-terracotta' : 'border-cream-dark text-espresso-light hover:border-cream-dark/60'}`}
                  >
                    Shopify
                  </button>
                </div>
              </div>

              <div className="flex gap-4 pt-2">
                <button 
                  type="button" 
                  onClick={() => setShowAddForm(false)}
                  className="flex-1 py-3 rounded-xl font-bold text-espresso-light hover:bg-cream transition-colors"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="flex-1 py-3 rounded-xl font-bold bg-espresso text-white hover:bg-espresso-light transition-colors shadow-lg"
                >
                  Start Setup
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopSelector;